import {
	BigNum,
	BN,
	FOUR,
	MarketType,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	QUOTE_SPOT_MARKET_INDEX,
	SpotMarketAccount,
	SpotMarketConfig,
	ZERO,
} from '@drift-labs/sdk';
import {
	DEFAULT_MAX_MARKET_LEVERAGE,
	ENUM_UTILS,
	MarketId,
	OpenPosition,
	valueIsBelowStepSize,
} from '@drift/common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAccountData from 'src/hooks/useAccountData';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useMarkPrice from 'src/hooks/useMarkPrice';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import NumLib from 'src/utils/NumLib';
import useWalletIsConnected from '../../../hooks/useWalletIsConnected';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { TradeFormDerivedState } from 'src/@types/types';
import {
	getPriceInputLabel,
	getShouldShowSecondaryPriceBox,
} from 'src/utils/trade';
import use24hMarketDataFromStore from 'src/hooks/use24hMarketDataFromStore';
import { useExceedsMaxSpotBalance } from 'src/hooks/useMaxSpotBalance';
import { useSetTradeFormQuoteSize } from 'src/components/TradeForm/hooks/useSetTradeFormInputState';
import useMarketStateStore from 'src/stores/useMarketStateStore';
import usePreviewOrder from './usePreviewOrder';
import { useCurrentMarketStepSize } from 'src/hooks/useMarketStepSize';
import { usePinTradeformSizeIfMaxLeverageSelected } from './usePinTradeformSizeIfMaxLeverageSelected';
import { getMessageInfoForTradeFormState } from '../utils/getMessageInfoForTradeformState';
import { useMaxLeverageHandlers } from './useHandleIfMaxLeverageSelected';
import { useSizeInputErrorMessage } from './useSizeInputErrorMessage';
import useModal from 'src/components/Modals/hooks/useModal';
import { useSyncPriceBoxes } from 'src/components/TradeForm/hooks/useSyncPriceBoxes';
import { useL2Orderbook } from 'src/hooks/query/orderbook/useL2Orderbook';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';
import useIsAccountMarginEnabled from 'src/components/TradeForm/hooks/useIsAccountMarginEnabled';

interface SpotTradeFormDerivedState extends Omit<
	TradeFormDerivedState,
	'showLeverageSlider'
> {
	showMarginCheckbox: boolean;
	isAccountMarginEnabled: boolean;
	isMarginCheckboxChecked: boolean;
	toggleIsMarginCheckboxChecked: () => void;
}

/**
 * A hook which does all of the logic to determine how the spot tradeform should behave based on its state
 */
export const useSpotTradeFormStateEngine = (): SpotTradeFormDerivedState => {
	// Generic App Hooks
	const currentAccount = useAccountData();
	const marginInfo = useCurrentAccountMarginInfo();
	const isAccountMarginEnabled = useIsAccountMarginEnabled();
	const setState = useDriftStore((s) => s.set);
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const tradeForm = useDriftStore((s) => s.tradeForm);
	const currentOrderType = useDriftStore((s) => s.tradeForm.orderType);
	const connected = useWalletIsConnected();
	const userAccountIsReady = useUserAccountIsReady();
	const driftClientIsReady = useDriftClientIsReady();
	const marketInfoStore = useMarketsInfoStore();
	const marketsData = use24hMarketDataFromStore();
	const { show } = useModal();

	const setQuoteFormValue = useSetTradeFormQuoteSize();

	// Hook states
	const [maxQuoteAvailable, setMaxQuoteAmountAvailable] = useState(0);
	const [maxBaseAvailable, setMaxBaseAmountAvailable] = useState(0);
	const [maxLeverageAvailable, setMaxLeverageAvailable] = useState(
		DEFAULT_MAX_MARKET_LEVERAGE
	);

	usePinTradeformSizeIfMaxLeverageSelected(maxQuoteAvailable, maxBaseAvailable);

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarketIndex = selectedMarket?.market.marketIndex;
	const spotMarketAccount = marketInfoStore?.getMarketInfoByIndexAndType(
		selectedMarketIndex ?? 0,
		MarketType.SPOT
	)?.account as SpotMarketAccount;
	const stepSize = useCurrentMarketStepSize();
	const { data: l2Orderbook } = useL2Orderbook(
		selectedMarket?.marketId ?? null
	);
	const l2State = l2Orderbook ?? { asks: [], bids: [] };
	const areOrderbooksLoaded = useMarketStateStore(
		(s) => s.latestUpdateSlot !== 0
	);

	const userWillExceedMaxSpotPositions =
		useExceedsMaxSpotBalance(selectedMarketIndex);
	const marketPriceBigNum = useMarkPrice();
	const marketPrice = parseFloat(marketPriceBigNum.toTradePrecision());

	const basePrecisionToUse = (selectedMarket?.market as SpotMarketConfig)
		.precisionExp;

	const currentPosition = useAccountTargetSpotBalance(
		selectedMarket?.market as SpotMarketConfig,
		currentAccount?.userKey ?? ''
	);

	const oraclePrice = useOraclePrice(selectedMarket?.marketId as MarketId);

	const [currentSettings] = useCurrentSettings();
	const { slippageTolerance: globalSlippageTolerance } = currentSettings;

	const [isMarginCheckboxChecked, setIsMarginCheckboxChecked] =
		useSyncLocalStorage('spotFormMarginCheckboxChecked', false);

	const toggleIsMarginCheckboxChecked = useCallback(() => {
		setIsMarginCheckboxChecked((curr) => !curr);
	}, [setIsMarginCheckboxChecked]);

	const previewOrderParams = usePreviewOrder();

	const priceImpact = previewOrderParams?.priceImpact;

	const {
		showPriceEstimateOracleDivergenceWarning: showOracleDivergenceWarning,
		exceedsLiquidity,
		markPrice,
	} = priceImpact ?? {};

	const currentMarketPriceBigNum = BigNum.from(markPrice, QUOTE_PRECISION_EXP);
	const currentMarketPrice = parseFloat(currentMarketPriceBigNum.print());

	const userHasPositionInMarket = !currentPosition.netBaseBalance.eqZero();

	const quoteSize = Number(tradeForm.quoteSizeStringValue);
	const priceBoxValue = Number(tradeForm.priceBoxStringValue);

	const freeCollateral = NumLib.formatBn.fromQuote(
		marginInfo?.freeCollateral ?? new BN(0)
	);

	const currentPositionDirection = currentPosition
		? currentPosition.netBaseBalance.isNeg()
			? 'short'
			: 'long'
		: // for logical purposes position is "long" if position is zero
			'long';

	const minTradeSizeForMarket =
		BigNum.from(
			spotMarketAccount?.minOrderSize,
			spotMarketAccount?.decimals
		)?.toNum() ?? 0;

	const priceInputLabel = getPriceInputLabel(currentOrderType);

	// Price Box Enabled
	const priceBoxEnabled = (() => {
		switch (currentOrderType) {
			case 'market':
				return false;
			case 'oracle':
				return false;
			default:
				return true;
		}
	})();

	const showSlippageTolerance = (() => {
		switch (currentOrderType) {
			case 'market':
				return true;
			default:
				return false;
		}
	})();

	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(currentOrderType);

	const showReduceOnly = (() => {
		const selectedDirection = tradeForm.side == 'buy' ? 'long' : 'short';
		switch (currentOrderType) {
			case 'market':
				return (
					userHasPositionInMarket &&
					currentPositionDirection != selectedDirection
				);
			default:
				return true;
		}
	})();

	const showPostOnly = useMemo(
		() => currentOrderType === 'limit' || currentOrderType === 'oracleLimit',
		[currentOrderType]
	);

	const showMarginCheckbox = useMemo(() => {
		switch (currentOrderType) {
			case 'takeProfitMarket':
			case 'stopLimit':
			case 'takeProfitLimit':
				return false;
			default:
				return !tradeForm.reduceOnly;
		}
	}, [currentOrderType, tradeForm.reduceOnly]);

	const isFormMarginEnabled =
		isAccountMarginEnabled && showMarginCheckbox && isMarginCheckboxChecked;

	// Show Reduce Position Slider
	const showReducePositionSlider = (() => {
		if (!tradeForm.reduceOnly) return false;

		const tradeFormDirection = tradeForm.side === 'buy' ? 'long' : 'short';

		switch (currentOrderType) {
			case 'market':
				return userHasPositionInMarket;
			default:
				return (
					userHasPositionInMarket &&
					tradeFormDirection !== currentPositionDirection
				);
		}
	})();

	useSyncPriceBoxes(
		tradeForm.side === 'buy' ? 'buy' : 'sell',
		tradeForm.priceBoxStringValue,
		tradeForm.secondaryPriceBoxStringValue,
		currentOrderType,
		selectedMarket
	);

	// Maintain max allowed quote and base state
	useEffect(() => {
		if (!userAccountIsReady || !driftClientIsReady || !currentAccount) {
			setMaxQuoteAmountAvailable(0);
			setMaxBaseAmountAvailable(0);
			return;
		}

		const maxQuoteAllowable = (() => {
			if (isFormMarginEnabled) {
				return currentAccount.client?.getMaxTradeSizeUSDCForSpot(
					selectedMarketIndex ?? 0,
					tradeForm.side === 'buy'
						? PositionDirection.LONG
						: PositionDirection.SHORT
				);
			}

			if (tradeForm.side === 'buy') {
				return currentAccount.client?.getSpotMarketAssetValue(
					QUOTE_SPOT_MARKET_INDEX
				);
			}

			const spotPositionValue =
				currentAccount.client?.getSpotPositionValue(selectedMarketIndex ?? 0) ??
				ZERO;
			return BN.max(ZERO, spotPositionValue);
		})();

		setMaxQuoteAmountAvailable(
			BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP).toNum()
		);

		if (oraclePrice && oraclePrice.gt(ZERO)) {
			setMaxBaseAmountAvailable(
				BigNum.from(maxQuoteAllowable, QUOTE_PRECISION_EXP)
					.scale(100_000, oraclePrice.toNum() * 100_000)
					.toNum()
			);
		}

		return;
	}, [
		freeCollateral,
		maxLeverageAvailable,
		selectedMarketIndex,
		tradeForm.side,
		marketPrice,
		userAccountIsReady,
		driftClientIsReady,
		connected,
		oraclePrice?.toString(),
		isFormMarginEnabled,
	]);

	// Maintain max allowed leverage state
	useEffect(() => {
		if (!userAccountIsReady || !driftClientIsReady || !connected) {
			setMaxLeverageAvailable(DEFAULT_MAX_MARKET_LEVERAGE);
			return;
		}

		if (!isFormMarginEnabled) {
			setMaxLeverageAvailable(0);
			return;
		}

		if (freeCollateral === 0) return;

		try {
			const newMaxLeverage = BigNum.from(
				currentAccount?.client?.getMaxLeverageForSpot(
					selectedMarketIndex ?? 0,
					tradeForm.side === 'buy'
						? PositionDirection.LONG
						: PositionDirection.SHORT
				),
				FOUR
			).toNum();

			setMaxLeverageAvailable(newMaxLeverage);
		} catch (e) {
			// little race condition here after wallet disconnect so wrap in try/catch
		}

		return;
	}, [
		selectedMarketIndex,
		currentAccount?.userKey,
		tradeForm.side,
		maxQuoteAvailable,
		freeCollateral,
		userAccountIsReady,
		driftClientIsReady,
		connected,
		isFormMarginEnabled,
	]);

	/* Keep tradeform slippage tolerance synced if updated from settings */
	useEffect(() => {
		setState((s) => {
			s.tradeForm.slippageTolerance =
				globalSlippageTolerance === 'dynamic'
					? 'dynamic'
					: Number(globalSlippageTolerance);
		});
	}, [globalSlippageTolerance]);

	// can't be reduce only if we're not showing it
	useEffect(() => {
		if (!showReduceOnly) {
			setReduceOnly(false);
		}
	}, [showReduceOnly, tradeForm.reduceOnly]);

	useEffect(() => {
		if (!showPostOnly) {
			setState((s) => {
				s.tradeForm.postOnly = false;
			});
		}
	}, [showPostOnly, setState]);

	const usingSpotReduceOnlyOverride = useRef(false);

	useEffect(() => {
		if (!isAccountMarginEnabled && tradeForm.side === 'sell') {
			usingSpotReduceOnlyOverride.current = true;
			setReduceOnly(true);
		} else if (usingSpotReduceOnlyOverride.current) {
			usingSpotReduceOnlyOverride.current = false;
			setReduceOnly(false);
		}
	}, [isAccountMarginEnabled, tradeForm.side]);

	// Handles rounding down trade form input size to the max allowed for the user
	const quoteSizeAvailableRef = useSyncedRef(tradeForm.quoteSizeStringValue);
	useEffect(() => {
		if (Number(quoteSizeAvailableRef.current) > maxQuoteAvailable) {
			setState((s) => {
				s.tradeForm.leadSide = 'quote';
			});

			setQuoteFormValue(maxQuoteAvailable.toFixed(2));
		}
	}, [maxQuoteAvailable, setState]);

	// sync price impact from preview order params for anywhere else that needs it
	useEffect(() => {
		setState((s) => {
			s.tradeForm.priceImpact = priceImpact;
		});
	}, [
		priceImpact?.marketIndex,
		priceImpact?.marketType,
		priceImpact?.entryPrice,
		priceImpact?.priceImpactInputBaseSize,
	]);

	const openAddAccountModal = () => {
		show({ type: 'new_subaccount', props: {} });
	};

	const setReduceOnly = (newVal: boolean) => {
		setState((s) => {
			s.tradeForm.reduceOnly = newVal;
		});
	};

	const market24hVol = marketsData?.find(
		(mkt) =>
			mkt.marketIndex === selectedMarketIndex &&
			ENUM_UTILS.match(mkt.marketType, selectedMarket?.marketType)
	)?.quoteVolume;

	const {
		messageType,
		shouldShowMessage,
		message,
		skipMessageInConfirmationModal,
		messageLink,
		messageLinkDescription,
		messageTitle,
		disableTradeButton,
		messageId,
	} = getMessageInfoForTradeFormState(
		currentOrderType,
		maxQuoteAvailable,
		stepSize,
		tradeForm,
		priceBoxValue,
		currentMarketPrice,
		userHasPositionInMarket,
		isGeoblocked,
		selectedMarket?.baseAssetSymbol() ?? '',
		exceedsLiquidity ?? false,
		MarketType.SPOT,
		openAddAccountModal,
		userWillExceedMaxSpotPositions,
		false,
		oraclePrice ? oraclePrice : undefined,
		l2State,
		areOrderbooksLoaded,
		currentPositionDirection,
		currentPosition.netBaseBalance.abs(),
		basePrecisionToUse,
		currentAccount?.name,
		market24hVol
	);

	const sizeErrorMessage = useSizeInputErrorMessage({
		baseSizeStringValue: tradeForm.baseSizeStringValue,
		stepSize: stepSize,
		maxBaseAvailable,
		minTradeSizeForMarket,
		quoteSizeStringValue: tradeForm.quoteSizeStringValue,
		marketSymbol: selectedMarket?.baseAssetSymbol() ?? '',
	});

	const tradeButtonIsDisabled =
		valueIsBelowStepSize(tradeForm.baseSizeStringValue, stepSize) ||
		disableTradeButton ||
		!tradeForm.baseSizeStringValue ||
		!connected ||
		!quoteSize ||
		!!sizeErrorMessage ||
		(priceBoxEnabled &&
			(currentOrderType === 'oracleLimit'
				? !tradeForm.priceBoxStringValue
				: !Number(tradeForm.priceBoxStringValue)));

	const { handleMaxLeverageClick, handleFormChange } = useMaxLeverageHandlers();

	const isReduceOnlyDisabled = showReduceOnly && tradeForm.postOnly;

	return {
		priceBoxHeading: priceInputLabel,
		priceBoxEnabled,
		showSlippageTolerance,
		showOrdersSplit: false,
		shouldShowMessage,
		messageType,
		message,
		skipMessageInConfirmationModal,
		showSecondaryPriceBox,
		isReduceOnlyDisabled,
		showReduceOnly,
		showPostOnly,
		tradeButtonIsDisabled,
		currentPosition: undefined as unknown as OpenPosition,
		maxQuoteAvailable,
		maxBaseAvailable,
		showReducePositionSlider,
		messageLink,
		messageLinkDescription,
		messageTitle,
		maxLeverageAvailable,
		messageId,
		showPriceEstimateOracleDivergenceWarning:
			(showOracleDivergenceWarning && currentOrderType === 'market') ?? false,
		handleMaxLeverageClick,
		handleFormChange,
		sizeErrorMessage,
		stepSize,
		displayLeverage: 0, // not needed for spot trades,
		showMarginCheckbox,
		isAccountMarginEnabled,
		isMarginCheckboxChecked,
		toggleIsMarginCheckboxChecked,
	};
};
